import Controller, { OnAfterFetch, OnAfterNavigate, OnBeforeSubmit, OnBlur, OnChange, OnMount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { AutocompleteAPI, CheckboxAPI, FormAPI, InputAPI, RadioGroupAPI, SelectableItem, ToastService, WizardAPI } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Value } from '@philips-internal/oswald-ui-core';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Inject } from '@odinjs/odin';

import moment from 'moment';
import { WoundsCreateAliases as Alias, FragmentNames } from '../../../../codegen';
import BaseController from '../../../BaseController';
import { Endpoint } from '../../../../constants/endpoints';
import { WizardSteps } from '../../../../constants/wizards';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { HealthProfessionalUser } from '../../../../app/authentication';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { LocatorTypes } from '../../../locator/locator-controller';
import timeService from '../../../../services/time-service';
import { WoundsOptionsId, WoundsRadioGroupId } from '../wounds-radio-group-data/wounds-radio-group-expressions';
import { DomainType, SearchType, WoundsResponse } from '../../types/wounds-dressings-types';
import DialogService from '../../../../services/dialog-service';

@Controller({ component: Alias.LAYOUT_WOUNDS_CREATE, fragment: FragmentNames.WOUNDS_CREATE })
export default class WoundsCreateController extends BaseController {

  @Inject(Alias.INPUT_START_DATE)
  private inputStartDateAPI!: InputAPI;

  @Inject(Alias.INPUT_WOUND_DAY)
  private inputWoundDayAPI!: InputAPI;

  @Inject(Alias.INPUT_WOUND_MONTH)
  private inputWoundMonthAPI!: InputAPI;

  @Inject(Alias.INPUT_WOUND_YEAR)
  private inputWoundYearAPI!: InputAPI;

  @Inject(Alias.FORM_ONE)
  private formOneAPI!: FormAPI;

  @Inject(Alias.FORM_TWO)
  private formTwoAPI!: FormAPI;

  @Inject(Alias.FORM_THREE)
  private formThreeAPI!: FormAPI;

  @Inject(Alias.FORM_FOUR)
  private formFourAPI!: FormAPI;

  @Inject(Alias.WIZARD)
  private wizardAPI!: WizardAPI;

  @Inject(Alias.AUTOCOMPLETE_COMPLEMENT)
  private autocompleteComplementAPI!: AutocompleteAPI;

  @Inject(Alias.AUTOCOMPLETE_WOUND_CLASSIFCATION)
  private autocompleteClassificationAPI!: AutocompleteAPI;

  @Inject(Alias.AUTOCOMPLETE_CLINIC)
  private autocompleteClinicAPI!: AutocompleteAPI;

  @Inject(Alias.AUTOCOMPLETE_SECTOR)
  private autocompleteSectorAPI!: AutocompleteAPI;

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'wounds-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private setRadioGroupValues(expressionId: number, radioGroup: RadioGroupAPI): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const defaultValue = WoundsOptionsId.SECTOR;
    const type = 'admittedInjury';
    HttpService.get(Endpoint.GET_PAIN_SCALE_DATA, { encounterId, expressionId, patientId })
      .then(response => {
        const responseBody = response.body as SelectableItem[];
        radioGroup.setData(responseBody);
        const defaultItemSelected = responseBody.find(item => item.value === defaultValue as unknown as string);
        this.formTwoAPI.setData({...this.formTwoAPI.getData(), [type]: defaultItemSelected});
        this.formTwoAPI.validate();
      }).catch((error) => {
        error?.body?.errorMessage && this.showToast(error.body.errorMessage, ToastLevel.ERROR)
      });
  }

  private handleStepDone(isStepDone: boolean, step: WizardSteps): void {
    this.wizardAPI.setStepDone(step, isStepDone);
  }

  private handleComplement(): void {
    this.autocompleteComplementAPI.setEnabled(false);
    this.autocompleteComplementAPI.setValue(undefined);
    const location = this.context.get('locationCode') as number;

    if (location) {
      this.autocompleteComplementAPI.setEnabled(true);
    }
  }

  private handleClassification(): void {
    this.autocompleteClassificationAPI.setEnabled(false);
    this.autocompleteClassificationAPI.setValue(undefined);
    const woundTypeCode = this.context.get('woundTypeCode') as SearchType;

    if (woundTypeCode) {
      this.autocompleteClassificationAPI.setEnabled(true);
    }
  }

  private handleReleaseWound(woundId: number): void {
    this.formOneAPI.setLoading(true);
    this.formTwoAPI.setLoading(true);
    this.formThreeAPI.setLoading(true);
    this.formFourAPI.setLoading(true);

    HttpService.put(Endpoint.RELEASE_WOUND, { sequenceNumberWound: woundId })
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch((err) => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
      }).finally(() => {
        this.formOneAPI.setLoading(false);
        this.formTwoAPI.setLoading(false);
        this.formThreeAPI.setLoading(false);
        this.formFourAPI.setLoading(false);
      })
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  private showModalSubmit(woundId: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.handleReleaseWound(woundId) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    })
  }

  private handleCreateWound(body: Record<string, Value>): void {
    this.formFourAPI.setLoading(true);

    HttpService.post(Endpoint.POST_WOUND, undefined, {body})
      .then((res) => {
        const { woundId } = res.body as WoundsResponse;
        this.showModalSubmit(woundId as number);
      }).catch((err) => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
      }).finally(() => this.formFourAPI.setLoading(false));
  }

  @OnMount(Alias.WIZARD)
  handleMountWizard(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const establishmentId = foundationContext.getEstablishment()?.id;
    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, establishmentId, locator: LocatorTypes.PROFESSIONAL_PERSON, patientId });
    timeService.getCurrentDateTime()
      .then(date => this.inputStartDateAPI.setValue(date))
      .catch(() => this.inputStartDateAPI.setValue(moment().format(ISO_FULL_DATE_FORMAT)));
  }

  @OnMount(Alias.RADIO_GROUP_ADMITTED_INJURY)
  handleMountRadioGroup(event: MountEvent): void {
    const radioGroup = event.target as RadioGroupAPI;
    this.setRadioGroupValues(WoundsRadioGroupId.GROUP_ID_ADMITTED_INJURED, radioGroup);
  }

  @OnMount(Alias.INPUT_WOUND_DAY)
  handleMountWoundDay(): void {
    const defaultDay = 0;
    this.inputWoundDayAPI.setValue(defaultDay);
  }

  @OnMount(Alias.INPUT_WOUND_MONTH)
  handleMountWoundMonth(): void {
    const defaultWoundMonth = 1;
    this.inputWoundMonthAPI.setValue(defaultWoundMonth);
  }

  @OnMount(Alias.INPUT_WOUND_YEAR)
  handleMountWoundYear(): void {
    const defaultWoundYear = 1;
    this.inputWoundYearAPI.setValue(defaultWoundYear);
  }

  @OnValidate(Alias.FORM_ONE)
  handleValidateFormOne(validationFormOne: ValidateEvent): void {
    this.handleStepDone(validationFormOne.valid, WizardSteps.STEP_ONE);
  }

  @OnValidate(Alias.FORM_TWO)
  handleValidateFormTwo(validationFormTwo: ValidateEvent): void {
    this.handleStepDone(validationFormTwo.valid, WizardSteps.STEP_TWO);
  }

  @OnValidate(Alias.FORM_THREE)
  handleValidateFormThree(validationFormThree: ValidateEvent): void {
    this.handleStepDone(validationFormThree.valid, WizardSteps.STEP_THREE);
  }

  @OnValidate(Alias.FORM_FOUR)
  handleValidateFormFour(validationFormFour: ValidateEvent): void {
    this.handleStepDone(validationFormFour.valid, WizardSteps.STEP_FOUR);
  }

  @OnAfterNavigate(Alias.WIZARD)
  handleOnMountWizardStepTwo(): void {
    this.wizardAPI.setStepDone(WizardSteps.STEP_THREE, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_TWO, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_THREE, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_FOUR, true);
  }

  @OnBlur(Alias.INPUT_START_DATE)
  handleBlurDate(): void {
    const inputDate = this.inputStartDateAPI.getValue() as string;
    const requiredLength = 19;
    if (inputDate.length < requiredLength && inputDate.length !== 0) {
      this.showToast('text.report-date-and-hour', ToastLevel.WARNING);
    }
  }

  @OnChange(Alias.AUTOCOMPLETE_WOUND_LOCATION)
  handleChangeComplement(event: ChangeEvent<SearchType>): void {
    const woundLocation = event.value as unknown as SearchType;
    this.context.set({ locationCode: woundLocation?.id });
    this.handleComplement();
  }

  @OnChange(Alias.AUTOCOMPLETE_WOUND_TYPE)
  handleChangeWoundType(event: ChangeEvent<SearchType>): void {
    const woundType = event.value as unknown as SearchType;
    this.context.set({ woundTypeCode: woundType?.id});
    this.handleClassification();
  }

  @OnAfterFetch(Alias.FETCH_CLINIC)
  handleFetchClinic(event: FetchEvent): void {
    const defaultValue = '1';
    const clinics = (event.data as Record<string, Value>[]);
    const defaultSelected = clinics.find(item => item.valueDomain === defaultValue);
    this.autocompleteClinicAPI.setValue(defaultSelected);
  }

  @OnAfterFetch(Alias.FETCH_COMPLEMENT)
  handleFetchComplement(event: FetchEvent): void {
    const complements = (event.data as Record<string, Value>[]);
    if (!complements.length) {
      this.autocompleteComplementAPI.setEnabled(false);
    }
  }

  @OnAfterFetch(Alias.FETCH_WOUND_CLASSIFICATION)
  handleFetchClassification(event: FetchEvent): void {
    const classifications = (event.data as Record<string, Value>[]);
    if (!classifications.length) {
      this.autocompleteClassificationAPI.setEnabled(false);
    }
  }

  @OnAfterFetch(Alias.FETCH_SECTOR)
  handleFetchSector(event: FetchEvent): void {
    const defaultValue = foundationContext.getSector()?.id;
    const sectors = (event.data as Record<string, Value>[]);
    const defaultSelected = sectors.find(item => item.id === defaultValue);
    this.autocompleteSectorAPI.setValue(defaultSelected);
  }

  @OnChange(Alias.CHECKBOX_UNKNOWN_START)
  handleChangeUnknownStart(event: ChangeEvent<CheckboxAPI>): void {
    const unknownStart = event.value as unknown as boolean;
    this.inputStartDateAPI.setValue('');
    this.inputStartDateAPI.setEnabled(!unknownStart);
  }

  @OnBeforeSubmit(Alias.WIZARD)
  handleBeforeSubmit(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const user                       = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const establishmentId = foundationContext.getEstablishment()?.id;
    const formDataOne                = this.formOneAPI.getRecord();
    const formDataTwo                = this.formTwoAPI.getRecord();
    const formDataThree              = this.formThreeAPI.getRecord();
    const formDataFour               = this.formFourAPI.getRecord();

    const body = {
      admittedInjury: (formDataTwo.admittedInjury as SelectableItem)?.key,
      allergies: formDataThree.allergies as string,
      classification: (formDataTwo.woundClassification as SearchType)?.id,
      clinic: (formDataOne.clinic as DomainType)?.valueDomain,
      complement: (formDataTwo.complement as SearchType)?.id,
      diagnosis: formDataOne.mainDiagnosis as string,
      establishmentCode: establishmentId as unknown as number,
      location: (formDataOne.woundLocation as SearchType)?.id,
      medicineAllergy: BooleanUtils.isPositiveValue(formDataThree.medicineAllergy as boolean),
      numberService: parseInt(encounterId as unknown as string),
      observation: formDataThree.observation as string,
      painSite: formDataThree.painSite as string,
      physicalPerson: patientId,
      previousHistory: BooleanUtils.isPositiveValue(formDataFour.previousHistory as boolean),
      professional: user.physicalPersonId,
      reportedPain: BooleanUtils.isPositiveValue(formDataThree.reportedPain as boolean),
      sector: (formDataOne.sector as SearchType)?.id,
      startDate: formDataOne.startDate ? moment(String(formDataOne.startDate)).utc().format(ISO_FULL_DATE_FORMAT) : undefined,
      unknownStart: BooleanUtils.isPositiveValue(formDataOne.unknownStart as boolean),
      whatUsed: formDataFour.whatUsed as string,
      whoTreated: formDataFour.whoTreated as string,
      woundDay: formDataFour.woundDay as number,
      woundMonth: formDataFour.woundMonth as number,
      woundShape: (formDataTwo.woundShape as DomainType)?.valueDomain,
      woundType: (formDataTwo.woundType as SearchType)?.id,
      woundYear: formDataFour.woundYear as number,
    } as Record<string, Value>;

    this.handleCreateWound(body);
  }
}
